/**
 * CL-PRODUCT-SELECTIONS-002 — Products & Selections foundation models (pure).
 *
 * No React, Firestore I/O, browser globals, or UI formatting.
 * Permission keys are not stored on product records.
 */

import { isValidProjectProductPathId } from "./projectProductPaths";
import {
  PRODUCT_OPTION_COMMERCIAL_PERSISTED_FIELD_KEYS,
  PRODUCT_OPTION_COMMERCIAL_STAFF_FIELDS,
  PRODUCT_OPTION_CLIENT_FACING_PRICE_FIELD_KEYS,
  normalizeProductCommercialFields,
  normalizeProductClientFacingPriceFields,
} from "./projectProductOptionCommercialModel";
import { normalizeProductSupplierOrganizationId } from "./projectProductOptionSupplierLinkModel";

export const PRODUCT_CATEGORY_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const PRODUCT_AREA_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const PRODUCT_REQUIREMENT_RECORD_STATUS = Object.freeze({
  ACTIVE: "active",
  ARCHIVED: "archived",
});

export const PRODUCT_REQUIREMENT_WORKFLOW_STATUS = Object.freeze({
  IDENTIFIED: "identified",
  RESEARCHING: "researching",
  SAMPLES_REQUESTED: "samples_requested",
  SAMPLES_RECEIVED: "samples_received",
  OPTIONS_PROPOSED: "options_proposed",
  CLIENT_REVIEW: "client_review",
  SELECTED: "selected",
  APPROVED: "approved",
  ORDERED: "ordered",
  BACKORDERED: "backordered",
  SHIPPED: "shipped",
  DELIVERED: "delivered",
  INSTALLED: "installed",
  DEFICIENCY: "deficiency",
  SUPERSEDED: "superseded",
  ARCHIVED: "archived",
});

export const PRODUCT_OPTION_STATUS = Object.freeze({
  DRAFT: "draft",
  ACTIVE: "active",
  REJECTED: "rejected",
  SUPERSEDED: "superseded",
  ARCHIVED: "archived",
});

export const PRODUCT_ORGANIZATION_ROLE = Object.freeze({
  SUPPLIER: "supplier",
  MANUFACTURER: "manufacturer",
  INSTALLER: "installer",
  SPECIFIER: "specifier",
  PURCHASER: "purchaser",
  OTHER_TRADE: "other_trade",
});

export const PRODUCT_FILE_REF_ROLE = Object.freeze({
  PRODUCT_PHOTO: "product_photo",
  SAMPLE_PHOTO: "sample_photo",
  SPEC_SHEET: "spec_sheet",
  WARRANTY_DOC: "warranty_doc",
  SHOP_DRAWING: "shop_drawing",
  INSTALLATION_DRAWING: "installation_drawing",
  OTHER: "other",
});

export const PRODUCT_SELECTION_EVENT_TYPE = Object.freeze({
  REQUIREMENT_CREATED: "requirement_created",
  OPTION_ADDED: "option_added",
  OPTION_UPDATED: "option_updated",
  SHORTLIST_CHANGED: "shortlist_changed",
  CLIENT_REVIEW_REQUESTED: "client_review_requested",
  CLIENT_PREFERENCE_RECORDED: "client_preference_recorded",
  STAFF_APPROVED: "staff_approved",
  CLIENT_APPROVED_LABEL: "client_approved_label",
  SELECTION_CHANGED: "selection_changed",
  ORDERED: "ordered",
  DELIVERY_UPDATED: "delivery_updated",
  SUPERSEDED: "superseded",
  ARCHIVED: "archived",
});

export const PRODUCT_SUBMISSION_STATUS = Object.freeze({
  PENDING: "pending",
  PROMOTED: "promoted",
  REJECTED: "rejected",
});

export const PRODUCT_SUBMISSION_PERSONA = Object.freeze({
  CLIENT: "client",
  TRADE: "trade",
  STAFF: "staff",
});

export const PRODUCT_REQUIREMENT_STAFF_ONLY_FIELDS = Object.freeze([
  "internalNotes",
  "internalCostSummary",
]);

export const PRODUCT_OPTION_STAFF_ONLY_FIELDS = Object.freeze([
  ...PRODUCT_OPTION_COMMERCIAL_STAFF_FIELDS,
]);

/** Permission keys and assignment-shaped fields must never be accepted on product records. */
export const PRODUCT_FORBIDDEN_AUTHORIZATION_FIELDS = Object.freeze([
  "canViewSuppliersAndSelections",
  "canViewProjectSuppliers",
  "canManageProjectSuppliers",
  "canViewFinancials",
  "canAccessFinancials",
  "canManageInvoices",
  "canViewInvoices",
  "canViewIssuedInvoices",
  "canViewClientTestInvoices",
  "canReviewDrawings",
  "permissions",
  "accessScope",
  "recipientUids",
]);

const CATEGORY_STATUSES = new Set(Object.values(PRODUCT_CATEGORY_STATUS));
const AREA_STATUSES = new Set(Object.values(PRODUCT_AREA_STATUS));
const REQUIREMENT_RECORD_STATUSES = new Set(Object.values(PRODUCT_REQUIREMENT_RECORD_STATUS));
const REQUIREMENT_WORKFLOW_STATUSES = new Set(Object.values(PRODUCT_REQUIREMENT_WORKFLOW_STATUS));
const OPTION_STATUSES = new Set(Object.values(PRODUCT_OPTION_STATUS));
const ORGANIZATION_ROLES = new Set(Object.values(PRODUCT_ORGANIZATION_ROLE));
const FILE_REF_ROLES = new Set(Object.values(PRODUCT_FILE_REF_ROLE));
const EVENT_TYPES = new Set(Object.values(PRODUCT_SELECTION_EVENT_TYPE));
const SUBMISSION_STATUSES = new Set(Object.values(PRODUCT_SUBMISSION_STATUS));
const SUBMISSION_PERSONAS = new Set(Object.values(PRODUCT_SUBMISSION_PERSONA));

function safeStr(value) {
  return String(value ?? "").trim();
}

function cloneInput(input) {
  if (input == null || typeof input !== "object" || Array.isArray(input)) return input;
  return { ...input };
}

function rejectForbiddenAuthorizationFields(input = {}) {
  const forbidden = PRODUCT_FORBIDDEN_AUTHORIZATION_FIELDS.filter((key) => key in input);
  if (forbidden.length) {
    return { ok: false, error: `Product records cannot include authorization fields: ${forbidden.join(", ")}.` };
  }
  return { ok: true };
}

function normalizeSortOrder(value, fallback = 0) {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return fallback;
  return Math.trunc(parsed);
}

function normalizeBoolean(value, fallback = false) {
  if (value === true || value === false) return value;
  return fallback;
}

function normalizeStringArray(values = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(values) ? values : []).forEach((entry) => {
    const next = safeStr(entry);
    if (!next || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  });
  return out;
}

function normalizeOrganizationIdList(values = []) {
  const seen = new Set();
  const out = [];
  (Array.isArray(values) ? values : []).forEach((entry) => {
    const next = safeStr(entry);
    if (!next || !isValidProjectProductPathId(next) || seen.has(next)) return;
    seen.add(next);
    out.push(next);
  });
  return out;
}

export function normalizeProductCategoryName(value) {
  return safeStr(value).replace(/\s+/g, " ");
}

export function isValidProductCategoryStatus(value) {
  return CATEGORY_STATUSES.has(safeStr(value));
}

export function validateProductCategoryDraft(draft = {}, { pathProjectId = "" } = {}) {
  const input = cloneInput(draft);
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const name = normalizeProductCategoryName(input.name);
  const sortOrder = normalizeSortOrder(input.sortOrder, 0);
  const status = safeStr(input.status) || PRODUCT_CATEGORY_STATUS.ACTIVE;

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Category projectId must match the document path." };
  }
  if (!name) return { ok: false, error: "Category name is required." };
  if (!isValidProductCategoryStatus(status)) {
    return { ok: false, error: "Choose a valid category status." };
  }

  const templateSourceId = safeStr(input.templateSourceId);
  if (templateSourceId && !isValidProjectProductPathId(templateSourceId)) {
    return { ok: false, error: "Invalid template source id." };
  }
  const parentCategoryId = safeStr(input.parentCategoryId);
  if (parentCategoryId && !isValidProjectProductPathId(parentCategoryId)) {
    return { ok: false, error: "Invalid parent category id." };
  }

  return {
    ok: true,
    projectId,
    name,
    sortOrder,
    status,
    description: safeStr(input.description) || null,
    parentCategoryId: parentCategoryId || null,
    templateSourceId: templateSourceId || null,
  };
}

export function buildProductCategoryRecord({
  draft = {},
  actorUid = "",
  existing = null,
  pathProjectId = "",
} = {}) {
  const validated = validateProductCategoryDraft(draft, { pathProjectId });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  const record = {
    projectId: validated.projectId,
    name: validated.name,
    sortOrder: validated.sortOrder,
    status: safeStr(existing?.status) === PRODUCT_CATEGORY_STATUS.ARCHIVED
      ? PRODUCT_CATEGORY_STATUS.ARCHIVED
      : validated.status,
    updatedByUid: safeStr(actorUid) || null,
  };
  if (validated.description) record.description = validated.description;
  if (validated.parentCategoryId) record.parentCategoryId = validated.parentCategoryId;
  if (validated.templateSourceId) record.templateSourceId = validated.templateSourceId;
  else if (existing?.templateSourceId) record.templateSourceId = existing.templateSourceId;

  return { ok: true, record };
}

export function normalizeProductAreaName(value) {
  return safeStr(value).replace(/\s+/g, " ");
}

export function isValidProductAreaStatus(value) {
  return AREA_STATUSES.has(safeStr(value));
}

export function validateProductAreaDraft(draft = {}, { pathProjectId = "" } = {}) {
  const input = cloneInput(draft);
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const name = normalizeProductAreaName(input.name);
  const sortOrder = normalizeSortOrder(input.sortOrder, 0);
  const status = safeStr(input.status) || PRODUCT_AREA_STATUS.ACTIVE;

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Area projectId must match the document path." };
  }
  if (!name) return { ok: false, error: "Area name is required." };
  if (!isValidProductAreaStatus(status)) {
    return { ok: false, error: "Choose a valid area status." };
  }

  const planningScopeAreaId = safeStr(input.planningScopeAreaId);
  if (planningScopeAreaId && !isValidProjectProductPathId(planningScopeAreaId)) {
    return { ok: false, error: "Invalid planning scope area id." };
  }

  return {
    ok: true,
    projectId,
    name,
    sortOrder,
    status,
    planningScopeAreaId: planningScopeAreaId || null,
    tags: normalizeStringArray(input.tags),
  };
}

export function buildProductAreaRecord({
  draft = {},
  actorUid = "",
  existing = null,
  pathProjectId = "",
} = {}) {
  const validated = validateProductAreaDraft(draft, { pathProjectId });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  const record = {
    projectId: validated.projectId,
    name: validated.name,
    sortOrder: validated.sortOrder,
    status: safeStr(existing?.status) === PRODUCT_AREA_STATUS.ARCHIVED
      ? PRODUCT_AREA_STATUS.ARCHIVED
      : validated.status,
    updatedByUid: safeStr(actorUid) || null,
  };
  if (validated.planningScopeAreaId) record.planningScopeAreaId = validated.planningScopeAreaId;
  else if (existing?.planningScopeAreaId) record.planningScopeAreaId = existing.planningScopeAreaId;
  if (validated.tags.length) record.tags = validated.tags;
  else if (Array.isArray(existing?.tags) && existing.tags.length) record.tags = [...existing.tags];

  return { ok: true, record };
}

export function normalizeProductSpecEntry(entry = {}) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const label = safeStr(entry.label);
  const value = safeStr(entry.value);
  const key = safeStr(entry.key) || null;
  const unit = safeStr(entry.unit) || null;
  if (!label || !value) return null;
  const normalized = { label, value };
  if (key) normalized.key = key;
  if (unit) normalized.unit = unit;
  return normalized;
}

export function normalizeProductSpecList(specs = []) {
  return (Array.isArray(specs) ? specs : [])
    .map((entry) => normalizeProductSpecEntry(entry))
    .filter(Boolean);
}

export const PRODUCT_FILE_REF_CAPTION_MAX_LENGTH = 200;

function looksLikePersistedFileUrl(value = "") {
  const text = safeStr(value).toLowerCase();
  if (!text) return false;
  return (
    text.startsWith("http://")
    || text.startsWith("https://")
    || text.startsWith("gs://")
    || text.startsWith("/")
    || text.includes("firebasestorage.googleapis.com")
  );
}

export function normalizeProductFileRefCaption(value = "") {
  const caption = safeStr(value).replace(/\s+/g, " ");
  if (!caption) return null;
  return caption.slice(0, PRODUCT_FILE_REF_CAPTION_MAX_LENGTH);
}

export function normalizeProductFileRef(entry = {}) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const fileId = safeStr(entry.fileId);
  const role = safeStr(entry.role) || PRODUCT_FILE_REF_ROLE.OTHER;
  if (!fileId || looksLikePersistedFileUrl(fileId) || !isValidProjectProductPathId(fileId)) return null;
  if (!FILE_REF_ROLES.has(role)) return null;
  const caption = normalizeProductFileRefCaption(entry.caption);
  const normalized = {
    fileId,
    role,
    sortOrder: normalizeSortOrder(entry.sortOrder, 0),
    clientVisible: normalizeBoolean(entry.clientVisible, false),
    tradeVisible: normalizeBoolean(entry.tradeVisible, false),
  };
  if (caption) normalized.caption = caption;
  return normalized;
}

export function normalizeProductFileRefList(fileRefs = []) {
  const seen = new Set();
  return (Array.isArray(fileRefs) ? fileRefs : [])
    .map((entry) => normalizeProductFileRef(entry))
    .filter(Boolean)
    .filter((entry) => {
      if (seen.has(entry.fileId)) return false;
      seen.add(entry.fileId);
      return true;
    });
}

export function normalizeProductOrganizationRole(entry = {}) {
  if (!entry || typeof entry !== "object" || Array.isArray(entry)) return null;
  const organizationId = safeStr(entry.organizationId);
  const role = safeStr(entry.role);
  if (!organizationId || !isValidProjectProductPathId(organizationId)) return null;
  if (!ORGANIZATION_ROLES.has(role)) return null;
  const normalized = { organizationId, role };
  const engagementId = safeStr(entry.engagementId);
  const workPackageId = safeStr(entry.workPackageId);
  const primaryContactId = safeStr(entry.primaryContactId);
  if (engagementId) normalized.engagementId = engagementId;
  if (workPackageId) normalized.workPackageId = workPackageId;
  if (primaryContactId) normalized.primaryContactId = primaryContactId;
  return normalized;
}

export function normalizeProductOrganizationRoleList(roles = []) {
  return (Array.isArray(roles) ? roles : [])
    .map((entry) => normalizeProductOrganizationRole(entry))
    .filter(Boolean);
}

export function deriveTradeOrganizationIds(organizationRoles = []) {
  return normalizeOrganizationIdList(
    normalizeProductOrganizationRoleList(organizationRoles).map((row) => row.organizationId)
  );
}

function validateOptionPointer(optionId, label) {
  const id = safeStr(optionId);
  if (!id) return { ok: true, value: null };
  if (!isValidProjectProductPathId(id)) {
    return { ok: false, error: `Invalid ${label}.` };
  }
  return { ok: true, value: id };
}

export function isValidProductRequirementWorkflowStatus(value) {
  return REQUIREMENT_WORKFLOW_STATUSES.has(safeStr(value));
}

export function isValidProductRequirementRecordStatus(value) {
  return REQUIREMENT_RECORD_STATUSES.has(safeStr(value));
}

export function validateProductRequirementDraft(draft = {}, { pathProjectId = "" } = {}) {
  const input = cloneInput(draft);
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const title = safeStr(input.title);
  const categoryId = safeStr(input.categoryId);
  const workflowStatus = safeStr(input.workflowStatus) || PRODUCT_REQUIREMENT_WORKFLOW_STATUS.IDENTIFIED;
  const status = safeStr(input.status) || PRODUCT_REQUIREMENT_RECORD_STATUS.ACTIVE;
  const sortOrder = normalizeSortOrder(input.sortOrder, 0);

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Requirement projectId must match the document path." };
  }
  if (!title) return { ok: false, error: "Requirement title is required." };
  if (!categoryId || !isValidProjectProductPathId(categoryId)) {
    return { ok: false, error: "Requirement category is required." };
  }
  if (!isValidProductRequirementWorkflowStatus(workflowStatus)) {
    return { ok: false, error: "Choose a valid requirement workflow status." };
  }
  if (!isValidProductRequirementRecordStatus(status)) {
    return { ok: false, error: "Choose a valid requirement record status." };
  }

  const areaId = safeStr(input.areaId);
  if (areaId && !isValidProjectProductPathId(areaId)) {
    return { ok: false, error: "Invalid area id." };
  }
  const selected = validateOptionPointer(input.selectedOptionId, "selected option id");
  if (!selected.ok) return selected;
  const approved = validateOptionPointer(input.approvedOptionId, "approved option id");
  if (!approved.ok) return approved;
  if (selected.value && approved.value && selected.value === approved.value) {
    // Separate pointers may reference the same option — allowed.
  }

  const organizationRoles = normalizeProductOrganizationRoleList(input.organizationRoles);
  const tradeOrganizationIds = normalizeOrganizationIdList(
    input.tradeOrganizationIds?.length
      ? input.tradeOrganizationIds
      : deriveTradeOrganizationIds(organizationRoles)
  );

  const allowanceItemId = safeStr(input.allowanceItemId);
  if (allowanceItemId && !isValidProjectProductPathId(allowanceItemId)) {
    return { ok: false, error: "Invalid allowance item id." };
  }
  const workPackageId = safeStr(input.workPackageId);
  if (workPackageId && !isValidProjectProductPathId(workPackageId)) {
    return { ok: false, error: "Invalid work package id." };
  }
  const linkedSupplierShareId = safeStr(input.linkedSupplierShareId);
  if (linkedSupplierShareId && !isValidProjectProductPathId(linkedSupplierShareId)) {
    return { ok: false, error: "Invalid linked supplier share id." };
  }

  return {
    ok: true,
    projectId,
    title,
    categoryId,
    workflowStatus,
    status,
    sortOrder,
    description: safeStr(input.description) || null,
    areaId: areaId || null,
    tags: normalizeStringArray(input.tags),
    workPackageId: workPackageId || null,
    organizationRoles,
    tradeOrganizationIds,
    selectedOptionId: selected.value,
    approvedOptionId: approved.value,
    clientVisible: normalizeBoolean(input.clientVisible, false),
    tradeVisible: normalizeBoolean(input.tradeVisible, false),
    clientDecisionNeeded: normalizeBoolean(input.clientDecisionNeeded, false),
    fileRefs: normalizeProductFileRefList(input.fileRefs),
    allowanceItemId: allowanceItemId || null,
    linkedSupplierShareId: linkedSupplierShareId || null,
    clientSummary: safeStr(input.clientSummary) || null,
    clientComparisonAllowanceAmount: input.clientComparisonAllowanceAmount ?? null,
    clientComparisonCurrency: safeStr(input.clientComparisonCurrency) || null,
    tradeSummary: safeStr(input.tradeSummary) || null,
    internalNotes: safeStr(input.internalNotes) || null,
    internalCostSummary: safeStr(input.internalCostSummary) || null,
    linkedScheduleTaskIds: normalizeStringArray(input.linkedScheduleTaskIds),
    decisionDueAt: input.decisionDueAt ?? null,
    quoteDueAt: input.quoteDueAt ?? null,
    orderByAt: input.orderByAt ?? null,
    orderedAt: input.orderedAt ?? null,
    requiredOnSiteAt: input.requiredOnSiteAt ?? null,
    estimatedDeliveryAt: input.estimatedDeliveryAt ?? null,
    confirmedDeliveryAt: input.confirmedDeliveryAt ?? null,
    actualDeliveryAt: input.actualDeliveryAt ?? null,
    installationTargetAt: input.installationTargetAt ?? null,
    installationCompletedAt: input.installationCompletedAt ?? null,
  };
}

export function buildProductRequirementRecord({
  draft = {},
  actorUid = "",
  existing = null,
  pathProjectId = "",
} = {}) {
  const validated = validateProductRequirementDraft(draft, { pathProjectId });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  const record = {
    projectId: validated.projectId,
    title: validated.title,
    categoryId: validated.categoryId,
    workflowStatus: validated.workflowStatus,
    status: safeStr(existing?.status) === PRODUCT_REQUIREMENT_RECORD_STATUS.ARCHIVED
      ? PRODUCT_REQUIREMENT_RECORD_STATUS.ARCHIVED
      : validated.status,
    sortOrder: validated.sortOrder,
    clientVisible: validated.clientVisible,
    tradeVisible: validated.tradeVisible,
    tradeOrganizationIds: validated.tradeOrganizationIds,
    updatedByUid: safeStr(actorUid) || null,
  };

  if (validated.description) record.description = validated.description;
  if (validated.areaId) record.areaId = validated.areaId;
  if (validated.tags.length) record.tags = validated.tags;
  if (validated.workPackageId) record.workPackageId = validated.workPackageId;
  if (validated.organizationRoles.length) record.organizationRoles = validated.organizationRoles;
  if (validated.selectedOptionId) record.selectedOptionId = validated.selectedOptionId;
  if (validated.approvedOptionId) record.approvedOptionId = validated.approvedOptionId;
  if (validated.clientDecisionNeeded) record.clientDecisionNeeded = true;
  if (validated.fileRefs.length) record.fileRefs = validated.fileRefs;
  if (validated.allowanceItemId) record.allowanceItemId = validated.allowanceItemId;
  if (validated.linkedSupplierShareId) record.linkedSupplierShareId = validated.linkedSupplierShareId;
  if (validated.clientSummary) record.clientSummary = validated.clientSummary;
  if (validated.clientComparisonAllowanceAmount != null && validated.clientComparisonAllowanceAmount !== "") {
    record.clientComparisonAllowanceAmount = validated.clientComparisonAllowanceAmount;
  }
  if (validated.clientComparisonCurrency) record.clientComparisonCurrency = validated.clientComparisonCurrency;
  if (validated.tradeSummary) record.tradeSummary = validated.tradeSummary;
  if (validated.internalNotes) record.internalNotes = validated.internalNotes;
  if (validated.internalCostSummary) record.internalCostSummary = validated.internalCostSummary;
  if (validated.linkedScheduleTaskIds.length) record.linkedScheduleTaskIds = validated.linkedScheduleTaskIds;

  const procurementFields = [
    "decisionDueAt",
    "quoteDueAt",
    "orderByAt",
    "orderedAt",
    "requiredOnSiteAt",
    "estimatedDeliveryAt",
    "confirmedDeliveryAt",
    "actualDeliveryAt",
    "installationTargetAt",
    "installationCompletedAt",
  ];
  procurementFields.forEach((key) => {
    if (validated[key] != null) record[key] = validated[key];
  });

  return { ok: true, record };
}

export function isValidProductOptionStatus(value) {
  return OPTION_STATUSES.has(safeStr(value));
}

export function normalizeProductOptionLabel(value) {
  return safeStr(value).replace(/\s+/g, " ");
}

function assignPersistedOptionField(record, key, validated, existing, { allowNullWrite = false } = {}) {
  if (Object.prototype.hasOwnProperty.call(validated, key)) {
    const value = validated[key];
    if (value === undefined) {
      if (existing && Object.prototype.hasOwnProperty.call(existing, key) && existing[key] != null && existing[key] !== "") {
        record[key] = existing[key];
      }
      return;
    }
    if (value === 0 || value === false) {
      record[key] = value;
      return;
    }
    if (value != null && value !== "") {
      record[key] = value;
      return;
    }
    if (allowNullWrite) {
      record[key] = null;
      return;
    }
  }
  if (existing && Object.prototype.hasOwnProperty.call(existing, key) && existing[key] != null && existing[key] !== "") {
    record[key] = existing[key];
  }
}

export function validateProductOptionDraft(draft = {}, {
  pathProjectId = "",
  pathRequirementId = "",
  existing = null,
} = {}) {
  const input = stripProductOptionSaveMarkers(cloneInput(draft));
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const requirementId = safeStr(input.requirementId);
  const label = normalizeProductOptionLabel(input.label || input.name);
  const optionStatus = safeStr(input.optionStatus)
    || safeStr(existing?.optionStatus)
    || PRODUCT_OPTION_STATUS.DRAFT;
  const recordLifecycle = safeStr(input.recordLifecycle) || safeStr(existing?.recordLifecycle) || null;
  const decisionStatus = safeStr(input.decisionStatus) || safeStr(existing?.decisionStatus) || null;

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Option projectId must match the document path." };
  }
  if (!requirementId || !isValidProjectProductPathId(requirementId)) {
    return { ok: false, error: "Requirement id is required." };
  }
  if (pathRequirementId && requirementId !== safeStr(pathRequirementId)) {
    return { ok: false, error: "Option requirementId must match the document path." };
  }
  if (!label) return { ok: false, error: "Option label is required." };
  if (!isValidProductOptionStatus(optionStatus)) {
    return { ok: false, error: "Choose a valid option status." };
  }

  const organizationId = safeStr(input.organizationId);
  if (organizationId && !isValidProjectProductPathId(organizationId)) {
    return { ok: false, error: "Invalid organization id." };
  }
  const includesSupplierFields = draftIncludesProductOptionSupplierFields(draft);
  const supplierOrganizationId = includesSupplierFields
    ? normalizeProductSupplierOrganizationId(input.supplierOrganizationId || organizationId)
    : normalizeProductSupplierOrganizationId(existing?.supplierOrganizationId || existing?.organizationId);
  if (includesSupplierFields && safeStr(input.supplierOrganizationId) && !supplierOrganizationId) {
    return { ok: false, error: "Invalid supplier organization id." };
  }

  const includesCommercialFields = draftIncludesProductOptionCommercialFields(draft);
  const commercial = includesCommercialFields
    ? normalizeProductCommercialFields(input)
    : { ok: true, ...pickExistingCommercialFields(existing) };
  if (!commercial.ok) return commercial;

  const includesClientPriceFields = draftIncludesProductOptionClientPriceFields(draft);
  const clientPrices = includesClientPriceFields
    ? normalizeProductClientFacingPriceFields({
      ...input,
      currency: commercial.currency || input.currency,
    })
    : { ok: true, ...pickExistingClientFacingPriceFields(existing) };
  if (!clientPrices.ok) return clientPrices;

  const sortOrder = normalizeSortOrder(input.sortOrder, 0);

  const specs = normalizeProductSpecList(input.specs);
  const fileRefs = normalizeProductFileRefList(input.fileRefs);
  const featuredRaw = safeStr(input.featuredPhotoFileId);
  let featuredPhotoFileId = null;
  if (featuredRaw) {
    if (!isValidProjectProductPathId(featuredRaw)) {
      return { ok: false, error: "Invalid featured photo reference." };
    }
    const featuredRef = fileRefs.find((row) => row.fileId === featuredRaw);
    const photoRoles = new Set([
      PRODUCT_FILE_REF_ROLE.PRODUCT_PHOTO,
      PRODUCT_FILE_REF_ROLE.SAMPLE_PHOTO,
    ]);
    if (!featuredRef || !photoRoles.has(featuredRef.role)) {
      return { ok: false, error: "Featured photo must reference an attached product or sample photo." };
    }
    featuredPhotoFileId = featuredRaw;
  }

  return {
    ok: true,
    projectId,
    requirementId,
    label,
    description: safeStr(input.description) || null,
    optionStatus,
    manufacturer: safeStr(input.manufacturer) || null,
    model: safeStr(input.model) || null,
    productName: safeStr(input.productName) || null,
    sku: safeStr(input.sku) || null,
    organizationId: includesSupplierFields
      ? (supplierOrganizationId || organizationId || null)
      : (normalizeProductSupplierOrganizationId(existing?.supplierOrganizationId || existing?.organizationId)
        || organizationId
        || null),
    supplierOrganizationId: includesSupplierFields ? supplierOrganizationId : normalizeProductSupplierOrganizationId(
      existing?.supplierOrganizationId || existing?.organizationId
    ),
    supplierDisplaySnapshot: includesSupplierFields
      ? (input.supplierDisplaySnapshot && typeof input.supplierDisplaySnapshot === "object"
        ? { ...input.supplierDisplaySnapshot }
        : null)
      : (existing?.supplierDisplaySnapshot && typeof existing.supplierDisplaySnapshot === "object"
        ? { ...existing.supplierDisplaySnapshot }
        : null),
    sortOrder,
    recordLifecycle,
    decisionStatus,
    decisionStatusBeforeArchive: input.decisionStatusBeforeArchive ?? existing?.decisionStatusBeforeArchive ?? null,
    specs,
    fileRefs,
    featuredPhotoFileId,
    productUrl: safeStr(input.productUrl) || null,
    clientVisible: normalizeBoolean(input.clientVisible, false),
    tradeVisible: normalizeBoolean(input.tradeVisible, false),
    isShortlisted: normalizeBoolean(input.isShortlisted, false),
    costMethod: commercial.costMethod,
    supplierQuote: commercial.supplierQuote,
    unitCost: commercial.unitCost,
    quantity: commercial.quantity,
    tax: commercial.tax,
    taxRate: commercial.taxRate,
    taxIncluded: commercial.taxIncluded,
    currency: commercial.currency,
    costBasis: commercial.costBasis,
    wastePercent: commercial.wastePercent,
    deliveryFreight: commercial.deliveryFreight,
    otherProductCost: commercial.otherProductCost,
    otherProductCostDescription: commercial.otherProductCostDescription,
    quoteCheckedAt: commercial.quoteCheckedAt,
    quoteReferenceNumber: commercial.quoteReferenceNumber,
    quotedTotalIncludesDelivery: commercial.quotedTotalIncludesDelivery,
    quotedTotalIncludesTax: commercial.quotedTotalIncludesTax,
    estimatedLeadTimeDays: commercial.estimatedLeadTimeDays,
    orderByAt: commercial.orderByAt,
    expectedDeliveryAt: commercial.expectedDeliveryAt,
    requiredOnSiteAt: commercial.requiredOnSiteAt,
    actualDeliveryAt: commercial.actualDeliveryAt,
    deliveryStatus: commercial.deliveryStatus,
    scheduleRisk: commercial.scheduleRisk,
    deliveryEstimateSource: commercial.deliveryEstimateSource,
    estimatedProductTotal: commercial.estimatedProductTotal,
    estimatedProductTotalBasisLabel: commercial.estimatedProductTotalBasisLabel,
    linkedBudgetItemId: commercial.linkedBudgetItemId,
    budgetForecastAppliedAt: commercial.budgetForecastAppliedAt,
    budgetForecastSourceOptionId: commercial.budgetForecastSourceOptionId,
    clientUnitPrice: clientPrices.clientUnitPrice ?? null,
    clientLineTotal: clientPrices.clientLineTotal ?? null,
    clientPriceCents: clientPrices.clientPriceCents ?? null,
    clientPriceCurrency: clientPrices.clientPriceCurrency ?? null,
    allowanceAmount: input.allowanceAmount ?? null,
    varianceFromAllowance: input.varianceFromAllowance ?? null,
    internalNotes: safeStr(input.internalNotes) || null,
    clientNotes: safeStr(input.clientNotes) || null,
    tradeNotes: safeStr(input.tradeNotes) || null,
  };
}

export const PRODUCT_OPTION_ATTACHMENT_FIELD_KEYS = Object.freeze([
  "fileRefs",
  "featuredPhotoFileId",
]);

export const PRODUCT_OPTION_COMMERCIAL_SAVE_MARKER = "_commercialFieldsIncluded";
export const PRODUCT_OPTION_SUPPLIER_SAVE_MARKER = "_supplierFieldsIncluded";

export function draftIncludesProductOptionCommercialFields(draft = {}) {
  if (draft[PRODUCT_OPTION_COMMERCIAL_SAVE_MARKER] === true) return true;
  return PRODUCT_OPTION_COMMERCIAL_PERSISTED_FIELD_KEYS.some((key) => (
    Object.prototype.hasOwnProperty.call(draft, key) && draft[key] !== undefined
  ));
}

export function draftIncludesProductOptionClientPriceFields(draft = {}) {
  if (draft[PRODUCT_OPTION_COMMERCIAL_SAVE_MARKER] === true) return true;
  return ["clientUnitPrice", "clientLineTotal", "clientPriceCents"].some((key) => (
    Object.prototype.hasOwnProperty.call(draft, key) && draft[key] !== undefined
  ));
}

function pickExistingClientFacingPriceFields(existing = {}) {
  const picked = {};
  if (!existing || typeof existing !== "object") return picked;
  PRODUCT_OPTION_CLIENT_FACING_PRICE_FIELD_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(existing, key)) {
      picked[key] = existing[key];
    }
  });
  return picked;
}

export function draftIncludesProductOptionSupplierFields(draft = {}) {
  if (draft[PRODUCT_OPTION_SUPPLIER_SAVE_MARKER] === true) return true;
  return ["supplierOrganizationId", "organizationId", "supplierDisplaySnapshot"].some((key) => (
    Object.prototype.hasOwnProperty.call(draft, key) && draft[key] !== undefined
  ));
}

function stripProductOptionSaveMarkers(input = {}) {
  const next = { ...input };
  delete next[PRODUCT_OPTION_COMMERCIAL_SAVE_MARKER];
  delete next[PRODUCT_OPTION_SUPPLIER_SAVE_MARKER];
  return next;
}

function pickExistingCommercialFields(existing = {}) {
  const picked = {};
  if (!existing || typeof existing !== "object") return picked;
  PRODUCT_OPTION_COMMERCIAL_PERSISTED_FIELD_KEYS.forEach((key) => {
    if (Object.prototype.hasOwnProperty.call(existing, key)) {
      picked[key] = existing[key];
    }
  });
  return picked;
}

export function draftIncludesProductOptionAttachmentFields(draft = {}) {
  return PRODUCT_OPTION_ATTACHMENT_FIELD_KEYS.some((key) => (
    Object.prototype.hasOwnProperty.call(draft, key) && draft[key] !== undefined
  ));
}

export function resolveStoredFeaturedPhotoFileId(fileRefs = [], featuredPhotoFileId = "") {
  const refs = normalizeProductFileRefList(fileRefs);
  const requested = safeStr(featuredPhotoFileId);
  const photoRoles = new Set([
    PRODUCT_FILE_REF_ROLE.PRODUCT_PHOTO,
    PRODUCT_FILE_REF_ROLE.SAMPLE_PHOTO,
  ]);
  if (requested) {
    const match = refs.find((row) => row.fileId === requested && photoRoles.has(row.role));
    if (match) return match.fileId;
  }
  const firstPhoto = refs.find((row) => photoRoles.has(row.role));
  return firstPhoto?.fileId || null;
}

export function preserveProductOptionAttachmentFields(source = {}) {
  const fileRefs = normalizeProductFileRefList(source.fileRefs);
  return {
    fileRefs,
    featuredPhotoFileId: resolveStoredFeaturedPhotoFileId(fileRefs, source.featuredPhotoFileId),
  };
}

export function buildProductOptionRecord({
  draft = {},
  actorUid = "",
  existing = null,
  pathProjectId = "",
  pathRequirementId = "",
} = {}) {
  const validated = validateProductOptionDraft(draft, { pathProjectId, pathRequirementId, existing });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  const attachmentFields = (existing && !draftIncludesProductOptionAttachmentFields(draft))
    ? preserveProductOptionAttachmentFields(existing)
    : {
      fileRefs: validated.fileRefs,
      featuredPhotoFileId: validated.featuredPhotoFileId,
    };

  const record = {
    projectId: validated.projectId,
    requirementId: validated.requirementId,
    label: validated.label,
    optionStatus: validated.optionStatus,
    recordLifecycle: validated.recordLifecycle,
    decisionStatus: validated.decisionStatus,
    sortOrder: validated.sortOrder || existing?.sortOrder || 0,
    clientVisible: validated.clientVisible,
    tradeVisible: validated.tradeVisible,
    isShortlisted: validated.isShortlisted,
    specs: validated.specs,
    fileRefs: attachmentFields.fileRefs,
    featuredPhotoFileId: attachmentFields.featuredPhotoFileId,
    updatedByUid: safeStr(actorUid) || null,
  };
  if (validated.decisionStatusBeforeArchive != null) {
    record.decisionStatusBeforeArchive = validated.decisionStatusBeforeArchive;
  }

  const includesCommercialFields = draftIncludesProductOptionCommercialFields(draft);
  const includesClientPriceFields = draftIncludesProductOptionClientPriceFields(draft);
  const includesSupplierFields = draftIncludesProductOptionSupplierFields(draft);
  const supplierFieldKeys = new Set([
    "organizationId",
    "supplierOrganizationId",
    "supplierDisplaySnapshot",
  ]);

  const optionalFields = [
    "description",
    "manufacturer",
    "model",
    "productName",
    "sku",
    "organizationId",
    "supplierOrganizationId",
    "supplierDisplaySnapshot",
    "productUrl",
    "clientUnitPrice",
    "clientLineTotal",
    "clientPriceCents",
    "clientPriceCurrency",
    "allowanceAmount",
    "varianceFromAllowance",
    "internalNotes",
    "clientNotes",
    "tradeNotes",
  ];
  const clientPriceFieldKeys = new Set([
    "clientUnitPrice",
    "clientLineTotal",
    "clientPriceCents",
    "clientPriceCurrency",
  ]);
  optionalFields.forEach((key) => assignPersistedOptionField(record, key, validated, existing, {
    allowNullWrite: supplierFieldKeys.has(key)
      ? includesSupplierFields
      : (clientPriceFieldKeys.has(key) ? includesClientPriceFields : false),
  }));

  [
    "costMethod",
    "supplierQuote",
    "unitCost",
    "quantity",
    "tax",
    "taxRate",
    "taxIncluded",
    "currency",
    "costBasis",
    "wastePercent",
    "deliveryFreight",
    "otherProductCost",
    "otherProductCostDescription",
    "quoteCheckedAt",
    "quoteReferenceNumber",
    "quotedTotalIncludesDelivery",
    "quotedTotalIncludesTax",
    "estimatedLeadTimeDays",
    "orderByAt",
    "expectedDeliveryAt",
    "requiredOnSiteAt",
    "actualDeliveryAt",
    "deliveryStatus",
    "scheduleRisk",
    "deliveryEstimateSource",
    "estimatedProductTotal",
    "estimatedProductTotalBasisLabel",
    "linkedBudgetItemId",
    "budgetForecastAppliedAt",
    "budgetForecastSourceOptionId",
  ].forEach((key) => assignPersistedOptionField(record, key, validated, existing, {
    allowNullWrite: includesCommercialFields,
  }));

  return { ok: true, record };
}

export function isValidProductSelectionEventType(value) {
  return EVENT_TYPES.has(safeStr(value));
}

export function validateProductSelectionEventDraft(draft = {}, {
  pathProjectId = "",
  pathRequirementId = "",
} = {}) {
  const input = cloneInput(draft);
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const requirementId = safeStr(input.requirementId);
  const eventType = safeStr(input.eventType);

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Event projectId must match the document path." };
  }
  if (!requirementId || !isValidProjectProductPathId(requirementId)) {
    return { ok: false, error: "Requirement id is required." };
  }
  if (pathRequirementId && requirementId !== safeStr(pathRequirementId)) {
    return { ok: false, error: "Event requirementId must match the document path." };
  }
  if (!isValidProductSelectionEventType(eventType)) {
    return { ok: false, error: "Choose a valid event type." };
  }

  const previousApproved = validateOptionPointer(input.previousApprovedOptionId, "previous approved option id");
  if (!previousApproved.ok) return previousApproved;
  const newApproved = validateOptionPointer(input.newApprovedOptionId, "new approved option id");
  if (!newApproved.ok) return newApproved;

  const previousWorkflowStatus = safeStr(input.previousWorkflowStatus);
  const newWorkflowStatus = safeStr(input.newWorkflowStatus);
  if (previousWorkflowStatus && !isValidProductRequirementWorkflowStatus(previousWorkflowStatus)) {
    return { ok: false, error: "Invalid previous workflow status." };
  }
  if (newWorkflowStatus && !isValidProductRequirementWorkflowStatus(newWorkflowStatus)) {
    return { ok: false, error: "Invalid new workflow status." };
  }

  return {
    ok: true,
    projectId,
    requirementId,
    eventType,
    occurredAt: input.occurredAt ?? null,
    actorUid: safeStr(input.actorUid) || null,
    actorRole: safeStr(input.actorRole) || null,
    previousApprovedOptionId: previousApproved.value,
    newApprovedOptionId: newApproved.value,
    previousWorkflowStatus: previousWorkflowStatus || null,
    newWorkflowStatus: newWorkflowStatus || null,
    reason: safeStr(input.reason) || null,
    clientVisible: normalizeBoolean(input.clientVisible, false),
    snapshot: input.snapshot && typeof input.snapshot === "object" ? { ...input.snapshot } : null,
  };
}

export function buildProductSelectionEventRecord({
  draft = {},
  pathProjectId = "",
  pathRequirementId = "",
} = {}) {
  const validated = validateProductSelectionEventDraft(draft, { pathProjectId, pathRequirementId });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  const record = {
    projectId: validated.projectId,
    requirementId: validated.requirementId,
    eventType: validated.eventType,
    clientVisible: validated.clientVisible,
  };
  if (validated.occurredAt != null) record.occurredAt = validated.occurredAt;
  if (validated.actorUid) record.actorUid = validated.actorUid;
  if (validated.actorRole) record.actorRole = validated.actorRole;
  if (validated.previousApprovedOptionId) record.previousApprovedOptionId = validated.previousApprovedOptionId;
  if (validated.newApprovedOptionId) record.newApprovedOptionId = validated.newApprovedOptionId;
  if (validated.previousWorkflowStatus) record.previousWorkflowStatus = validated.previousWorkflowStatus;
  if (validated.newWorkflowStatus) record.newWorkflowStatus = validated.newWorkflowStatus;
  if (validated.reason) record.reason = validated.reason;
  if (validated.snapshot) record.snapshot = validated.snapshot;

  return { ok: true, record };
}

export function isValidProductSubmissionStatus(value) {
  return SUBMISSION_STATUSES.has(safeStr(value));
}

export function isValidProductSubmissionPersona(value) {
  return SUBMISSION_PERSONAS.has(safeStr(value));
}

export function validateProductSubmissionDraft(draft = {}, {
  pathProjectId = "",
  pathRequirementId = "",
} = {}) {
  const input = cloneInput(draft);
  const authCheck = rejectForbiddenAuthorizationFields(input);
  if (!authCheck.ok) return authCheck;

  const projectId = safeStr(input.projectId);
  const requirementId = safeStr(input.requirementId);
  const submittedByUid = safeStr(input.submittedByUid);
  const submittedByPersona = safeStr(input.submittedByPersona);
  const submissionStatus = safeStr(input.submissionStatus) || PRODUCT_SUBMISSION_STATUS.PENDING;

  if (!projectId) return { ok: false, error: "Missing project." };
  if (pathProjectId && projectId !== safeStr(pathProjectId)) {
    return { ok: false, error: "Submission projectId must match the document path." };
  }
  if (!requirementId || !isValidProjectProductPathId(requirementId)) {
    return { ok: false, error: "Requirement id is required." };
  }
  if (pathRequirementId && requirementId !== safeStr(pathRequirementId)) {
    return { ok: false, error: "Submission requirementId must match the document path." };
  }
  if (!submittedByUid || !isValidProjectProductPathId(submittedByUid)) {
    return { ok: false, error: "Submitted-by uid is required." };
  }
  if (!isValidProductSubmissionPersona(submittedByPersona)) {
    return { ok: false, error: "Choose a valid submission persona." };
  }
  if (!isValidProductSubmissionStatus(submissionStatus)) {
    return { ok: false, error: "Choose a valid submission status." };
  }

  const payload = input.payload && typeof input.payload === "object" ? { ...input.payload } : null;
  if (!payload) return { ok: false, error: "Submission payload is required." };

  const payloadValidation = validateProductOptionDraft(
    { ...payload, projectId, requirementId },
    { pathProjectId, pathRequirementId }
  );
  if (!payloadValidation.ok) {
    return { ok: false, error: `Submission payload is invalid: ${payloadValidation.error}` };
  }

  return {
    ok: true,
    projectId,
    requirementId,
    submittedByUid,
    submittedByPersona,
    submissionStatus,
    payload,
  };
}

export function buildProductSubmissionRecord({
  draft = {},
  pathProjectId = "",
  pathRequirementId = "",
} = {}) {
  const validated = validateProductSubmissionDraft(draft, { pathProjectId, pathRequirementId });
  if (!validated.ok) return { ok: false, error: validated.error, record: null };

  return {
    ok: true,
    record: {
      projectId: validated.projectId,
      requirementId: validated.requirementId,
      submittedByUid: validated.submittedByUid,
      submittedByPersona: validated.submittedByPersona,
      submissionStatus: validated.submissionStatus,
      payload: validated.payload,
    },
  };
}

export function productRecordContainsStaffOnlyFields(record = {}, staffOnlyFields = []) {
  const keys = Array.isArray(staffOnlyFields) ? staffOnlyFields : [];
  return keys.filter((key) => {
    const value = record?.[key];
    return value != null && safeStr(value) !== "";
  });
}
